import React from 'react'
import {
  GeckoComponentPath,
  GeckoComponentRect,
  GeckoFeatureModelType,
  GeckoHorizontalWeld,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoVerticalWeld,
} from 'types'

import { FeatureLabel3D } from './procedural/FeatureLabel3D'
import {
  SurfaceLineFeature3D,
  SurfaceLineSelectionType,
} from './procedural/SurfaceLineFeature3D'

/**
 * A weld line on a tank floor. Annular rings supersede the plates beneath them,
 * so the line is clipped to the region inside the innermost ring and hidden
 * entirely when it only runs underneath the rings.
 */
export const FloorWeldFeature3D: React.FC<{
  weld: GeckoVerticalWeld | GeckoHorizontalWeld
  floorRadius: GeckoMeasurement
  annularRingInnerRadius: GeckoMeasurement | undefined
}> = ({ weld, floorRadius, annularRingInnerRadius }) => {
  const isVertical = weld.type === GeckoFeatureModelType.VerticalWeld

  const { path, labelCoords } = React.useMemo(() => {
    const clipped = clipLineToInsideOfAnnularRing(
      weld.feature_geometry,
      isVertical,
      floorRadius,
      annularRingInnerRadius
    )
    return {
      path: !clipped
        ? undefined
        : isVertical
          ? GeckoComponentPath.CreateFromVerticalLine(clipped)
          : GeckoComponentPath.CreateFromHorizontalLine(clipped),
      labelCoords: clipped?.end,
    }
  }, [weld.feature_geometry, isVertical, floorRadius, annularRingInnerRadius])

  return !path || !labelCoords ? null : (
    <group name={weld.id}>
      <SurfaceLineFeature3D
        path={path}
        featureModel={weld}
        lineSelectionType={SurfaceLineSelectionType.SelectableFeatureLine}
      />
      <FeatureLabel3D
        feature={weld}
        coords={labelCoords}
        anchor={'top left'}
        showBoundaryLabelIn2D
      />
    </group>
  )
}

function clipLineToInsideOfAnnularRing(
  lineGeometry: GeckoComponentRect,
  isVertical: boolean,
  floorRadius: GeckoMeasurement,
  annularRingInnerRadius: GeckoMeasurement | undefined
): GeckoComponentRect | undefined {
  if (annularRingInnerRadius === undefined) return lineGeometry

  const geometry = lineGeometry.convertTo(
    GeckoMeasurementUnit.Meters,
    GeckoMeasurementUnit.Meters
  )
  const radius = annularRingInnerRadius.convertTo(
    GeckoMeasurementUnit.Meters
  ).num
  // The floor bounds span its diameter, so the center is at (radius, radius)
  const center = floorRadius.convertTo(GeckoMeasurementUnit.Meters).num

  const constantAxis = isVertical ? geometry.x : geometry.y
  const clippedAxis = isVertical ? geometry.y : geometry.x

  const distanceFromCenter = Math.abs(constantAxis.start.num - center)
  if (distanceFromCenter >= radius) return undefined

  const halfChord = Math.sqrt(radius * radius - distanceFromCenter ** 2)
  const clippedMin = Math.max(clippedAxis.min.num, center - halfChord)
  const clippedMax = Math.min(clippedAxis.max.num, center + halfChord)
  if (clippedMin >= clippedMax) return undefined

  const constant = constantAxis.start.num
  const [xStart, yStart, xEnd, yEnd] = isVertical
    ? [constant, clippedMin, constant, clippedMax]
    : [clippedMin, constant, clippedMax, constant]
  return GeckoComponentRect.CreateFromXYs(
    geometry.componentID,
    xStart,
    yStart,
    xEnd,
    yEnd,
    GeckoMeasurementUnit.Meters,
    GeckoMeasurementUnit.Meters
  )
}
